import { Flex, Icon, Text, useColorModeValue } from '@chakra-ui/react';
import { MdOutlineApartment, MdOutlinePlace } from 'react-icons/md';
import { translate } from '../../i18n';
import { ReadGate, scopePromptI18nKey } from './readState';

/**
 * What a single CONTROL renders when its read was never issued.
 *
 * The third member of a family that already had two: `ScopePrompt` is the
 * page-level render (48px icon, 40px of vertical padding, its own block of
 * screen), and `ChartReadState` is the inside-a-chart-box render. Neither fits
 * a court picker inside a modal's form stack — dropping `ScopePrompt` there
 * pushes the form the operator came for off screen, which is the same objection
 * `InlineErrorState` records against `ErrorState`, one state over.
 *
 * So this is `InlineErrorState`'s sibling for the fourth read state: same
 * in-flow rhythm, same "say the one true thing" job, different sentence. Where
 * `InlineErrorState` says *we asked and did not get an answer*, this says *we
 * have not asked, and here is what is missing*.
 *
 * Why the sentence matters more than the styling. A control whose read was
 * never issued renders, by default, as an EMPTY control — an empty Select, an
 * empty checkbox group, an empty chip row — which is a confident statement that
 * the venue has nothing to offer. React Query v4 reports a disabled query as
 * `isLoading: true` with `fetchStatus: 'idle'`, so the other default (drive the
 * loading affordance off the raw flag) is a spinner that never resolves. Both
 * were live on the `useGetCourts` consumers until run 134; see that hook's
 * docblock.
 *
 * Deliberately NOT `EmptyState`: that component is this dashboard's vocabulary
 * for "we asked and there is nothing", which is exactly the sentence this state
 * exists to stop. And deliberately no CTA — retrying changes nothing until a
 * scope is chosen, and the control that chooses it is the picker in the navbar,
 * not anything inside this form.
 *
 * The string is a BORROWED i18n key. `scopePromptI18nKey` owns the choice so
 * this component, `ScopePrompt` and `ChartReadState` cannot drift apart (D59: a
 * private copy of a shared decision is a fork, and this one would fail silently
 * because the wrong key still renders a real translated sentence). Un-borrowing
 * is one edit in `readState.ts` — see the constants there.
 */
export default function InlineScopePrompt({ gate }: { gate: ReadGate }) {
    const promptColor = useColorModeValue('secondaryGray.600', 'secondaryGray.500');

    return (
        <Flex align='center' gap='8px' px={3} py={2}>
            <Icon
                as={gate === 'location' ? MdOutlinePlace : MdOutlineApartment}
                w='20px'
                h='20px'
                color={promptColor}
                aria-hidden='true'
            />
            <Text color={promptColor} fontSize='sm' fontWeight='500'>
                {translate(scopePromptI18nKey(gate) as any)}
            </Text>
        </Flex>
    );
}
